import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { doc, onSnapshot, setDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { useAuth } from "./CombinedAuthContext";
import { firestore, functions } from "../FirebaseConfig";
import { getTierByKey, TIER_KEYS } from "../constants/plans";

const PlansContext = createContext(null);

// Legacy plan-name constants, kept for back-compat with a couple of callers.
const PLAN_IDS = {
  TRAVELER: "Traveler",
  TRAVELER_PLUS: "Traveler Plus",
  BUSINESS_OWNER: "Ship Captain (Business Owner)",
};

export const usePlans = () => useContext(PlansContext);

/**
 * PlansProvider — membership tier + actions, backed by Stripe Billing.
 *
 * The tier is read in realtime from users/{uid} (syncedTravelerTierKey /
 * syncedBusinessTierKey), which the Stripe subscription webhook keeps in sync.
 * Subscribing goes through Stripe Checkout; managing/upgrading/downgrading/
 * cancelling goes through the Stripe Customer Portal.
 */
export const PlansProvider = ({ children }) => {
  const auth = useAuth();
  const uid = (auth && auth.firebaseUser && auth.firebaseUser.uid) || null;

  const [membership, setMembership] = useState({
    travelerTierKey: null,
    businessTierKey: null,
    subs: {},
    sevenDayPassExpiresMs: null,
    loading: true,
  });
  const [busy, setBusy] = useState(false);
  // Pre-launch kill switch (config/membership.publicSubscriptionsDisabled;
  // public-read). Realtime so an admin toggle takes effect without a reload.
  const [publicCheckoutDisabled, setPublicCheckoutDisabled] = useState(false);

  useEffect(() => {
    const ref = doc(firestore, "config", "membership");
    return onSnapshot(
      ref,
      (snap) => setPublicCheckoutDisabled(snap.exists() ? !!snap.data().publicSubscriptionsDisabled : false),
      () => setPublicCheckoutDisabled(false) // fail-open on read error
    );
  }, []);

  useEffect(() => {
    if (!uid) {
      setMembership({
        travelerTierKey: null,
        businessTierKey: null,
        subs: {},
        sevenDayPassExpiresMs: null,
        loading: false,
      });
      return undefined;
    }
    const ref = doc(firestore, "users", uid);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const d = snap.exists() ? snap.data() : {};
        setMembership({
          // Free-tier floor: any signed-in user is at least a Deckhand.
          // DECKHAND has no Stripe Product by design, so syncedTravelerTierKey
          // is null for everyone without a PAID subscription. Mirrors the
          // server floor in functions/utils.js resolveCallerTier.
          travelerTierKey: d.syncedTravelerTierKey || TIER_KEYS.DECKHAND,
          // No floor for business — a business tier is opt-in, not a default.
          // But once claimed, the free Crew Listing must resolve here or the
          // client disagrees with the server floor in resolveCallerTier.
          businessTierKey:
            d.syncedBusinessTierKey ||
            (d.freeMembershipTier === "crew" ? TIER_KEYS.CREW_LISTING : null),
          subs: d.membershipSubs || {},
          sevenDayPassExpiresMs: d.sevenDayPassExpiresMs || null,
          loading: false,
        });
      },
      (err) => {
        console.error("[PlansContext] membership listener error:", err);
        setMembership((m) => ({ ...m, loading: false }));
      },
    );
    return () => unsub();
  }, [uid]);

  const getActiveTravelerTier = useCallback(
    () => getTierByKey(membership.travelerTierKey),
    [membership.travelerTierKey],
  );
  const getActiveBusinessTier = useCallback(
    () => getTierByKey(membership.businessTierKey),
    [membership.businessTierKey],
  );

  const isTravelerPlus = () => membership.travelerTierKey === TIER_KEYS.TRAVELER_PLUS;
  const isTraveler = () =>
    membership.travelerTierKey === TIER_KEYS.ADVENTURER_ANNUAL ||
    membership.travelerTierKey === TIER_KEYS.TRAVELER_PLUS;
  // PAID business tiers only — keep this narrow, it feeds hasPaidSubscription.
  const isBusinessOwner = () =>
    membership.businessTierKey === TIER_KEYS.FEATURED_CAPTAIN ||
    membership.businessTierKey === TIER_KEYS.PORT_SPONSOR;
  // Any business tier including the free Crew Listing. This is what gates the
  // business dashboard and listing editor — a free Crew member belongs there.
  const isBusinessMember = () => !!membership.businessTierKey;
  // isMember is "has any tier, including the free Deckhand floor" — with the
  // floor above this is true for every signed-in user. Use hasPaidSubscription
  // for anything that should cost money to reach.
  const isMember = () =>
    !!membership.travelerTierKey || !!membership.businessTierKey;
  const hasMembership = () => isMember();
  // True only for a PAID plan in either audience (excludes the free Deckhand
  // and Crew Listing floors). This is the gate for premium-only surfaces.
  const hasPaidSubscription = () =>
    isTraveler() ||
    membership.travelerTierKey === TIER_KEYS.ADVENTURER_7DAY ||
    isBusinessOwner();

  // Claim the free Crew Listing tier. There is no Stripe Product for a $0 tier,
  // so this is a direct write rather than Checkout. `freeMembershipTier` is the
  // same field the server floor reads (functions/utils.js resolveCallerTier),
  // and firestore.rules already scopes users/{uid} writes to the owner, so a
  // callable would add deploy surface without adding a trust boundary.
  const claimFreeBusinessTier = async () => {
    if (!uid) throw new Error("Sign in to claim the free Crew Listing.");
    if (membership.businessTierKey) return membership.businessTierKey;
    setBusy(true);
    try {
      await setDoc(
        doc(firestore, "users", uid),
        { freeMembershipTier: "crew" },
        { merge: true },
      );
      // The onSnapshot listener above picks the change up and re-resolves tier.
      return TIER_KEYS.CREW_LISTING;
    } finally {
      setBusy(false);
    }
  };

  // Start a subscription (or the one-time 7-Day pass) via Stripe Checkout.
  const startCheckout = async (tierKey, interval) => {
    if (publicCheckoutDisabled) {
      // Belt-and-suspenders; the callable enforces this server-side too.
      throw new Error("Subscriptions are temporarily unavailable.");
    }
    setBusy(true);
    try {
      const fn = httpsCallable(functions, "createMembershipCheckout");
      const res = await fn({ tierKey, interval });
      const url = res && res.data && res.data.url;
      if (url) window.location.href = url;
      return url;
    } finally {
      setBusy(false);
    }
  };

  // Open the Stripe Customer Portal to manage / upgrade / downgrade / cancel.
  const openBillingPortal = async (audience) => {
    if (publicCheckoutDisabled) {
      // Kill switch also blocks the portal (upgrade/manage path). Server enforces this too.
      throw new Error("Subscriptions are temporarily unavailable.");
    }
    setBusy(true);
    try {
      const fn = httpsCallable(functions, "createBillingPortalSession");
      const res = await fn({ audience });
      const url = res && res.data && res.data.url;
      if (url) window.location.href = url;
      return url;
    } finally {
      setBusy(false);
    }
  };

  const value = {
    loading: membership.loading,
    busy,
    // Tier + membership checks (source: Stripe → Firestore sync).
    isMember,
    hasMembership,
    hasPaidSubscription,
    isTraveler,
    isTravelerPlus,
    isBusinessOwner,
    isBusinessMember,
    getActiveTravelerTier,
    getActiveBusinessTier,
    membershipSubs: membership.subs,
    sevenDayPassExpiresMs: membership.sevenDayPassExpiresMs,
    // Pre-launch kill switch — storefront CTAs read this to disable subscribe.
    publicCheckoutDisabled,
    // Actions.
    startCheckout,
    openBillingPortal,
    claimFreeBusinessTier,
    // Back-compat shims for legacy callers.
    memberOrders: [],
    publicPlans: [],
    PLAN_IDS,
    refreshPlans: async () => true,
    getTimeSinceLastFetch: () => "live",
  };

  return (
    <PlansContext.Provider value={value}>{children}</PlansContext.Provider>
  );
};

export default PlansContext;
